
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, TrendingDown, AlertTriangle, Users, MessageSquare, Heart } from 'lucide-react';

interface BrandMonitoringDashboardProps {
  results: any[];
}

const BrandMonitoringDashboard = ({ results }: BrandMonitoringDashboardProps) => {
  // Calculate metrics from results
  const totalMentions = results.length;
  const positiveMentions = results.filter(r => r.analysis?.sentiment === 'positive').length;
  const negativeMentions = results.filter(r => r.analysis?.sentiment === 'negative').length;
  const neutralMentions = results.filter(r => r.analysis?.sentiment === 'neutral').length;
  
  const sentimentScore = totalMentions > 0 ? ((positiveMentions - negativeMentions) / totalMentions * 100) : 0;
  const positivePercentage = totalMentions > 0 ? (positiveMentions / totalMentions * 100) : 0;
  const negativePercentage = totalMentions > 0 ? (negativeMentions / totalMentions * 100) : 0;

  const alertItems = results.filter(r => 
    r.analysis?.sentiment === 'negative' || 
    (r.analysis?.toxicity_score && r.analysis.toxicity_score > 0.7)
  );

  return (
    <div className="w-full max-w-none space-y-6 overflow-hidden">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{"\n"}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Mentions</p>
                <p className="text-2xl font-bold text-gray-900">{totalMentions}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-600" />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              <span className="text-green-600">↑ 12%</span> from last week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Sentiment Score</p>
                <p className="text-2xl font-bold text-gray-900">{sentimentScore.toFixed(1)}</p>
              </div>
              {sentimentScore >= 0 ? (
                <TrendingUp className="h-8 w-8 text-green-600" />
              ) : (
                <TrendingDown className="h-8 w-8 text-red-600" />
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Overall brand sentiment
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Reach</p>
                <p className="text-2xl font-bold text-gray-900">2.4M</p>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Estimated audience reached
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Engagement</p>
                <p className="text-2xl font-bold text-gray-900">8.3K</p>
              </div>
              <Heart className="h-8 w-8 text-red-600" />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Total likes, shares, comments
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Sentiment Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Sentiment Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  Positive ({positiveMentions})
                </span>
                <span>{positivePercentage.toFixed(1)}%</span>
              </div>
              <Progress value={positivePercentage} className="h-2" />
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="flex items-center">
                  <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
                  Neutral ({neutralMentions})
                </span>
                <span>{((neutralMentions / totalMentions) * 100 || 0).toFixed(1)}%</span>
              </div>
              <Progress value={(neutralMentions / totalMentions) * 100 || 0} className="h-2" />
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                  Negative ({negativeMentions})
                </span>
                <span>{negativePercentage.toFixed(1)}%</span>
              </div>
              <Progress value={negativePercentage} className="h-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Alerts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            <span>Alerts & Issues</span>
            {alertItems.length > 0 && (
              <Badge variant="destructive">{alertItems.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alertItems.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No alerts at the moment. All mentions are looking good!
            </p>
          ) : (
            <div className="space-y-4">
              {alertItems.slice(0, 5).map((item, idx) => (
                <div key={idx} className="p-4 border-l-4 border-red-500 bg-red-50">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline">{item.content.platform}</Badge>
                      <Badge variant="secondary">@{item.content.author}</Badge>
                      <Badge variant="destructive">
                        {item.analysis.sentiment === 'negative' ? 'Negative' : 'High Toxicity'}
                      </Badge>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date().toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-900 text-sm">{item.content.text}</p>
                  {item.analysis.reasons && (
                    <div className="mt-2">
                      <span className="text-xs font-medium text-red-700">Reasons: </span>
                      {item.analysis.reasons.slice(0, 2).map((reason: string, i: number) => (
                        <Badge key={i} variant="destructive" className="text-xs mr-1">
                          {reason}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Influencers */}
      <Card>
        <CardHeader>
          <CardTitle>Top Influencers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {results.slice(0, 5).map((result, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                    {result.content.author.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium">@{result.content.author}</p>
                    <p className="text-sm text-gray-500">{result.content.platform}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium">{result.content.metadata?.followers || 'N/A'}</p>
                  <p className="text-sm text-gray-500">followers</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrandMonitoringDashboard;
